import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage = 10,
  onPageChange,
  showItemsPerPage = false,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Don't render if there's only one page or no items
  if (totalPages <= 1 && !showItemsPerPage) {
    return null;
  }

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-white dark:bg-dark-card border-t border-gray-200 dark:border-dark-border transition-colors duration-200">
      {/* Items info */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600 dark:text-dark-muted">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </span>
        
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-dark-muted">Per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
              className="border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={`p-1.5 rounded transition-colors ${
              currentPage === 1
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
            title="First page"
          >
            <ChevronsLeft size={18} />
          </button>

          {/* Previous page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-1.5 rounded transition-colors ${
              currentPage === 1
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-2">
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-gray-400 dark:text-dark-muted">...</span>
                ) : (
                  <button
                    onClick={() => onPageChange(page)}
                    className={`min-w-[32px] px-2 py-1 rounded text-sm font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-primary-600 dark:bg-primary-500 text-white'
                        : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-1.5 rounded transition-colors ${
              currentPage === totalPages
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>

          {/* Last page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`p-1.5 rounded transition-colors ${
              currentPage === totalPages
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-dark-muted hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
            title="Last page"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

// Custom hook for pagination logic
export const usePagination = (items, initialItemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(initialItemsPerPage);

  // Reset to page 1 when items change significantly
  React.useEffect(() => {
    const totalPages = Math.ceil(items.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [items.length, itemsPerPage, currentPage]);

  const paginatedItems = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  }, [items, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const resetPage = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalItems: items.length,
    handlePageChange,
    handleItemsPerPageChange,
    resetPage,
  };
};

export default Pagination;








